import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { finalize } from 'rxjs/operators'

import { ApplicationSetting } from '@core/model/enums/application-setting.enum'
import { CommonModule } from '@angular/common'
import { ExchangeRate } from '@app/features/scope-overview/model/exchange-rate.model'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { FormsModule } from '@angular/forms'
import { Money } from '@app/features/scope-overview/model/money.model'
import { PipesModule } from '@shared/pipe/index.module'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeVersion } from '@core/model/scope-version'
import { SharedModule } from '@shared/shared.module'
import { SowRetainedHoursStats } from '@app/features/scope-overview/model/sow-retained-hours-stats.model'

@Component({
  selector: 'details-overview',
  standalone: true,
  imports: [CommonModule, PipesModule, FormsModule, ScopeUiDropdownComponent, SharedModule],
  templateUrl: './details-overview.component.html',
})
export class DetailsOverviewComponent implements OnChanges {
  expandedMode = false
  versionNumbers: number[] = []
  selectedVersion: number
  selectedExchangeRate: ExchangeRate

  @Input() scopeVersion!: ScopeVersion

  @Input() scopeRetainedHoursStats!: ScopeRetainedHoursStats

  @Input() sowRetainedHoursStats!: SowRetainedHoursStats

  @Input() trafficMetadataLookupFields!: any

  @Input() thirdPartyCostBalance!: Money

  @Input() exchangeRates!: ExchangeRate[]

  @Output() onVersionChange = new EventEmitter<number>()

  private readonly CURRENCY_CONVERSION_KEY = 'scope_internal_currency_conversion'
  private isCurrencyUpdating = false

  constructor(
    public exchangeRateService: ExchangeRateService,
    private scopeOverviewService: ScopeOverviewService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.generateVersionNumbers()
    this.selectedVersion = this.scopeVersion?.version

    if (this.scopeVersion && (changes.scopeVersion || changes.exchangeRates)) {
      this.loadSavedCurrency()
    }
  }

  changeVersion() {
    this.onVersionChange.emit(this.selectedVersion)
  }

  changeCurrency() {
    this.exchangeRateService.exchangeRate = this.selectedExchangeRate

    if (!this.scopeVersion?.identity?.id || !this.selectedExchangeRate?.toCurrencyCode) return

    this.isCurrencyUpdating = true

    this.scopeOverviewService
      .updateCurrency(this.scopeVersion.identity.id, this.selectedExchangeRate.toCurrencyCode)
      .pipe(finalize(() => (this.isCurrencyUpdating = false)))
      .subscribe((updatedScope) => {
        if (this.scopeVersion) {
          this.scopeVersion.dynamicFields = updatedScope.dynamicFields
        }
      })
  }

  private loadSavedCurrency(): void {
    if (this.isCurrencyUpdating) return

    const savedCurrencyCode = this.scopeVersion?.dynamicFields?.[this.CURRENCY_CONVERSION_KEY]
    if (!savedCurrencyCode) return

    const savedExchangeRate = this.exchangeRates?.find((rate) => rate.toCurrencyCode === savedCurrencyCode)

    if (savedExchangeRate) {
      this.selectedExchangeRate = savedExchangeRate
      this.exchangeRateService.exchangeRate = savedExchangeRate
      this.cdr.detectChanges()
    }
  }

  versionDisplayFn = (version: number) => {
    return version.toString()
  }

  exchangeRateDisplayFn = (exchangeRate: ExchangeRate) => {
    return exchangeRate.toCurrencyCode
  }

  generateVersionNumbers() {
    const currentVersionNumber = this.scopeVersion.identity?.currentVersionNumber
    this.versionNumbers = Array.from({ length: currentVersionNumber }, (_, i) => i + 1)
  }

  getExternalDropdownDynamicFieldValue = (setting: any) => {
    if (typeof setting === 'object' && setting !== null) {
      return (
        setting?.Name ||
        setting?.Id ||
        setting?.name ||
        setting?.entityId ||
        setting?.ProjectName ||
        setting?.ProjectNumber
      )
    }
    return setting || '--'
  }

  protected readonly ApplicationSetting = ApplicationSetting
}
